/**
 * Generate Jig thumbnail module.
 * Attempts to produce an AI-enhanced thumbnail from the source model image and
 * falls back to uploading the original thumbnail whenever enhancement is unsafe.
 */
import { Filters } from '@/utils/filters'
import axios from 'axios'
import type { AxiosRequestConfig } from 'axios'
import type {
  DispatchAiProxyRequest,
  DispatchAiProxyResponse,
  GeminiImagePromptPayload,
} from './generate-jig.types'
import { isRequestCanceled } from './generate-jig.utils'

declare var consoleError: Function
declare var consoleLog: Function

interface GenerateJigThumbnailContext {
  apiDomain: string
  buildAuthHeaders(includeAppName: boolean, extraHeaders?: Record<string, string>): Record<string, string>
  buildJigApiHeaders(includeAppName: boolean, extraHeaders?: Record<string, string>): Record<string, string>
  getRequestConfig(config?: AxiosRequestConfig): AxiosRequestConfig
}

interface ThumbnailSourceData {
  imageBase64: string
  mimeType: string
}

function buildThumbnailPrompt(userQuestion: string, displayName: string): string {
  const cleanQuestion = userQuestion.trim()
  const modelName = displayName && displayName.trim().length > 0 ? displayName.trim() : '3D model'
  const description = cleanQuestion.length > 0 ? cleanQuestion : 'not provided'

  return (
    'Task: Make an enhanced photorealistic rendered version of this 3D model screenshot in a realistic environment. ' +
    'For context: The filename is ' +
    modelName +
    ' and the description is ' +
    description +
    '. Output: Maintain the camera angle, perspective, and object orientation. ' +
    'For the 3D models, use the colours as defined in the screenshot. ' +
    'Do not render any text from the file description into the image. ' +
    'Avoid overly bright lit or white outputs so that any white text overlaid on them is readable. ' +
    'No text in the generated image.'
  )
}

function buildGeminiImagePromptPayload(imageBase64: string, mimeType: string, promptText: string): GeminiImagePromptPayload {
  return {
    contents: [
      {
        role: 'user',
        parts: [
          {
            inlineData: {
              mimeType: mimeType,
              data: imageBase64,
            },
          },
          {
            text: promptText,
          },
        ],
      },
    ],
    generationConfig: {
      responseModalities: ['Image'],
      imageConfig: {
        aspectRatio: '4:3',
      },
    },
  }
}

function buildDispatchThumbnailRequest(
  imageBase64: string,
  mimeType: string,
  userQuestion: string,
  displayName: string
): DispatchAiProxyRequest {
  const promptPayload = buildGeminiImagePromptPayload(
    imageBase64,
    mimeType,
    buildThumbnailPrompt(userQuestion, displayName)
  )

  return {
    provider: 'gemini',
    model: 'gemini-2.5-flash-image',
    prompt: JSON.stringify(promptPayload),
    html: false,
  }
}

function resolveThumbnailSource(libraryThumbnailUrl: string): { url: string; requiresAuth: boolean } {
  const mappedUrl = Filters.thumbnailURLMap(libraryThumbnailUrl || '')
  const requiresAuth = String(libraryThumbnailUrl || '').indexOf('/tenants/v1') === 0

  return {
    url: mappedUrl,
    requiresAuth: requiresAuth,
  }
}

function resolveThumbnailMimeType(contentType: string, url: string): string {
  const normalizedHeader = contentType.toLowerCase()
  if (normalizedHeader.indexOf('image/png') >= 0) {
    return 'image/png'
  }

  if (normalizedHeader.indexOf('image/jpeg') >= 0 || normalizedHeader.indexOf('image/jpg') >= 0) {
    return 'image/jpeg'
  }

  if (normalizedHeader.indexOf('image/webp') >= 0) {
    return 'image/webp'
  }

  const lowerUrl = String(url || '').toLowerCase()
  if (lowerUrl.indexOf('.jpg') >= 0 || lowerUrl.indexOf('.jpeg') >= 0) {
    return 'image/jpeg'
  }

  if (lowerUrl.indexOf('.webp') >= 0) {
    return 'image/webp'
  }

  return 'image/png'
}

async function downloadThumbnailForAi(
  ctx: GenerateJigThumbnailContext,
  imageUrl: string,
  requiresAuth: boolean
): Promise<ThumbnailSourceData> {
  const requestConfig: AxiosRequestConfig = {
    responseType: 'arraybuffer',
  }

  if (requiresAuth) {
    requestConfig.headers = ctx.buildJigApiHeaders(true)
  }

  const response = await axios.get(imageUrl, ctx.getRequestConfig(requestConfig))
  const responseHeaders = response && response.headers ? response.headers : {}
  const contentTypeHeader = responseHeaders['content-type'] || responseHeaders['Content-Type'] || ''

  return {
    imageBase64: arrayBufferToBase64(response.data),
    mimeType: resolveThumbnailMimeType(String(contentTypeHeader), imageUrl),
  }
}

function arrayBufferToBase64(imageBuffer: ArrayBuffer): string {
  const bytes = new Uint8Array(imageBuffer)
  const chunkSize = 8192
  const parts: string[] = []

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length))
    parts.push(String.fromCharCode.apply(null, chunk as any))
  }

  return btoa(parts.join(''))
}

function stripBase64Prefix(base64Value: string): string {
  const trimmedValue = String(base64Value || '').trim()
  const dataUrlMatch = trimmedValue.match(/^data:.*?;base64,(.*)$/i)

  if (dataUrlMatch && dataUrlMatch[1]) {
    return dataUrlMatch[1]
  }

  return trimmedValue
}

function base64ToArrayBuffer(base64Value: string): ArrayBuffer {
  const normalizedBase64 = stripBase64Prefix(base64Value)
  const binary = atob(normalizedBase64)
  const bytes = new Uint8Array(binary.length)

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }

  return bytes.buffer
}

function parseJsonStringIfNeeded(value: any): any {
  if (typeof value !== 'string') {
    return value
  }

  const trimmed = value.trim()
  const maybeJson = trimmed.indexOf('{') === 0 || trimmed.indexOf('[') === 0
  if (!maybeJson) {
    return value
  }

  try {
    return JSON.parse(trimmed)
  } catch (_error) {
    return value
  }
}

function findInlineImageInPayload(payload: any): { base64Data: string; mimeType: string } | null {
  const queue: any[] = [payload]
  const visited = new Set<object>()

  while (queue.length > 0) {
    const current = queue.shift()
    if (current === null || current === undefined) {
      continue
    }

    if (typeof current === 'string') {
      const parsedString = parseJsonStringIfNeeded(current)
      if (parsedString !== current) {
        queue.push(parsedString)
      }
      continue
    }

    if (Array.isArray(current)) {
      for (let i = 0; i < current.length; i++) {
        queue.push(current[i])
      }
      continue
    }

    if (typeof current !== 'object') {
      continue
    }

    if (visited.has(current)) {
      continue
    }
    visited.add(current)

    const currentData = current as any
    const inlineData = currentData.inlineData || currentData.inline_data
    if (inlineData && typeof inlineData.data === 'string' && inlineData.data.length > 0) {
      const mimeType = String(inlineData.mimeType || inlineData.mime_type || 'image/png').toLowerCase()
      return {
        base64Data: inlineData.data,
        mimeType: mimeType,
      }
    }

    const knownBase64Keys = ['b64_json', 'base64', 'imageBase64', 'image_base64']
    for (let i = 0; i < knownBase64Keys.length; i++) {
      const key = knownBase64Keys[i]
      if (typeof currentData[key] === 'string' && currentData[key].length > 0) {
        const mimeType = String(currentData.mimeType || currentData.mime_type || 'image/png').toLowerCase()
        return {
          base64Data: currentData[key],
          mimeType: mimeType,
        }
      }
    }

    const keys = Object.keys(currentData)
    for (let i = 0; i < keys.length; i++) {
      queue.push(currentData[keys[i]])
    }
  }

  return null
}

function extractGeneratedImageFromDispatchResponse(responseData: any): { imageBuffer: ArrayBuffer; mimeType: string } | null {
  if (!responseData) {
    consoleLog('Dispatch thumbnail response is empty.')
    return null
  }

  const parsedData = parseJsonStringIfNeeded(responseData) as DispatchAiProxyResponse

  if (parsedData && Array.isArray(parsedData.files) && parsedData.files.length > 0) {
    consoleLog('Dispatch thumbnail response includes files: ' + parsedData.files.length)
    const firstFile = parsedData.files[0]
    if (firstFile && typeof firstFile.data_base64 === 'string' && firstFile.data_base64.length > 0) {
      const mimeType = String(firstFile.content_type || parsedData.content_type || 'image/png').toLowerCase()
      consoleLog('Dispatch thumbnail first file MIME type: ' + mimeType)
      return {
        imageBuffer: base64ToArrayBuffer(firstFile.data_base64),
        mimeType: mimeType,
      }
    }
  }

  if (parsedData && parsedData.json_raw) {
    const inlineImageFromJsonRaw = findInlineImageInPayload(parsedData.json_raw)
    if (inlineImageFromJsonRaw) {
      consoleLog('Dispatch thumbnail parsed image from json_raw payload.')
      return {
        imageBuffer: base64ToArrayBuffer(inlineImageFromJsonRaw.base64Data),
        mimeType: inlineImageFromJsonRaw.mimeType,
      }
    }
  }

  // Fallback kept intentionally to tolerate dispatch/provider response-shape drift.
  consoleLog('Dispatch thumbnail response missing files array, using fallback image parser.')
  const extractedImage = findInlineImageInPayload(parsedData)
  if (!extractedImage) {
    consoleLog('Dispatch thumbnail fallback parser found no image data.')
    return null
  }

  const normalizedBase64 = stripBase64Prefix(extractedImage.base64Data)
  if (!normalizedBase64) {
    return null
  }

  return {
    imageBuffer: base64ToArrayBuffer(normalizedBase64),
    mimeType: extractedImage.mimeType,
  }
}

function getImageTypeHeaderValue(): string {
  return '.png'
}

function startsWithBytes(bytes: Uint8Array, signature: number[]): boolean {
  if (bytes.length < signature.length) {
    return false
  }

  for (let i = 0; i < signature.length; i++) {
    if (bytes[i] !== signature[i]) {
      return false
    }
  }

  return true
}

function isIsoBmffWithBrand(bytes: Uint8Array, brands: string[]): boolean {
  if (bytes.length < 12) {
    return false
  }

  const isFtyp = bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70
  if (!isFtyp) {
    return false
  }

  const brand = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]).toLowerCase()
  return brands.indexOf(brand) >= 0
}

function detectImageFormatFromBuffer(imageBuffer: ArrayBuffer): string {
  const bytes = new Uint8Array(imageBuffer)
  if (bytes.length < 12) {
    return 'unknown'
  }

  if (startsWithBytes(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return 'image/png'
  }

  if (startsWithBytes(bytes, [0xff, 0xd8, 0xff])) {
    return 'image/jpeg'
  }

  if (
    startsWithBytes(bytes, [0x52, 0x49, 0x46, 0x46]) &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return 'image/webp'
  }

  if (startsWithBytes(bytes, [0x47, 0x49, 0x46, 0x38])) {
    return 'image/gif'
  }

  if (startsWithBytes(bytes, [0x42, 0x4d])) {
    return 'image/bmp'
  }

  if (isIsoBmffWithBrand(bytes, ['avif', 'avis'])) {
    return 'image/avif'
  }

  return 'unknown'
}

async function uploadThumbnailToJig(
  ctx: GenerateJigThumbnailContext,
  jigId: string,
  version: string,
  imageBuffer: ArrayBuffer,
  isFirstThumbnail: boolean
): Promise<string> {
  const formData = new FormData()
  const imageBlob = new Blob([imageBuffer], { type: 'image/png' })
  formData.append('image', imageBlob, 'jig-' + jigId + '-color')

  const requestConfig = {
    params: {
      cl: '6x6',
      mode: 'medium',
      version: version,
    },
    headers: ctx.buildJigApiHeaders(false, {
      'image-type': getImageTypeHeaderValue(),
    }),
  }

  try {
    consoleLog('Uploading generated thumbnail to Jig ' + jigId + ' via ' + (isFirstThumbnail ? 'POST' : 'PUT'))
    const response = isFirstThumbnail
      ? await axios.post(ctx.apiDomain + '/v2/me/jigs/' + jigId + '/thumbnail', formData, ctx.getRequestConfig(requestConfig))
      : await axios.put(ctx.apiDomain + '/v2/me/jigs/' + jigId + '/thumbnail', formData, ctx.getRequestConfig(requestConfig))

    const data = response && response.data ? response.data : {}
    return String(data.thumbnailURL || data.ThumbnailURL || '')
  } catch (error: any) {
    if (isRequestCanceled(error)) {
      throw error
    }
    if (error && error.response && error.response.status === 409) {
      if (isFirstThumbnail) {
        consoleLog('Thumbnail POST returned 409 for Jig ' + jigId + '; retrying with PUT.')
        const response = await axios.put(ctx.apiDomain + '/v2/me/jigs/' + jigId + '/thumbnail', formData, ctx.getRequestConfig(requestConfig))
        const data = response && response.data ? response.data : {}
        return String(data.thumbnailURL || data.ThumbnailURL || '')
      }

      consoleLog('Thumbnail PUT returned 409 for Jig ' + jigId + '; retrying with POST.')
      const response = await axios.post(ctx.apiDomain + '/v2/me/jigs/' + jigId + '/thumbnail', formData, ctx.getRequestConfig(requestConfig))
      const data = response && response.data ? response.data : {}
      return String(data.thumbnailURL || data.ThumbnailURL || '')
    }
    throw error
  }
}

async function uploadSourceThumbnailFallback(
  ctx: GenerateJigThumbnailContext,
  jigId: string,
  sourceThumbnailData: ThumbnailSourceData,
  reason: string,
  jigVersion: string,
  isFirstThumbnail: boolean
): Promise<void> {
  const sourceImageBuffer = base64ToArrayBuffer(sourceThumbnailData.imageBase64)
  const detectedSourceMimeType = detectImageFormatFromBuffer(sourceImageBuffer)
  if (detectedSourceMimeType !== 'image/png') {
    consoleError('Fallback source thumbnail is not a valid PNG image binary. Upload skipped.')
    return
  }

  if (!jigVersion) {
    consoleError('Fallback source thumbnail upload skipped due to missing Jig version.')
    return
  }

  consoleLog('Falling back to source model thumbnail upload. reason=' + reason)
  await uploadThumbnailToJig(
    ctx,
    jigId,
    jigVersion,
    sourceImageBuffer,
    isFirstThumbnail
  )
}

/**
 * Downloads the source thumbnail, requests an AI-enhanced PNG, validates the
 * generated payload, and uploads either the enhanced or source image as needed.
 */
export async function tryGenerateAndUploadThumbnail(
  ctx: GenerateJigThumbnailContext,
  jigId: string,
  userQuestion: string,
  displayName: string,
  libraryThumbnailUrl: string,
  jigVersion: string,
  thumbnailUrl: string
): Promise<void> {
  if (!ctx.apiDomain || !libraryThumbnailUrl) {
    return
  }

  const isFirstThumbnail = thumbnailUrl.indexOf('placeholder') >= 0

  let sourceThumbnailData: ThumbnailSourceData | null = null

  try {
    const thumbnailSource = resolveThumbnailSource(libraryThumbnailUrl)
    if (!thumbnailSource.url) {
      return
    }

    sourceThumbnailData = await downloadThumbnailForAi(ctx, thumbnailSource.url, thumbnailSource.requiresAuth)
    const dispatchRequest = buildDispatchThumbnailRequest(
      sourceThumbnailData.imageBase64,
      sourceThumbnailData.mimeType,
      userQuestion,
      displayName
    )

    const dispatchResponse = await axios.post(
      ctx.apiDomain + '/dispatch/v1/ai',
      dispatchRequest,
      ctx.getRequestConfig({
        params: {
          async: false,
          max_retries: 3,
          max_timeout_seconds: 30,
        },
        headers: ctx.buildAuthHeaders(false),
      })
    )
    const dispatchResponseData = dispatchResponse && dispatchResponse.data ? dispatchResponse.data : {}
    const dispatchFiles = dispatchResponseData && Array.isArray(dispatchResponseData.files) ? dispatchResponseData.files.length : 0
    const dispatchContentType = dispatchResponseData && dispatchResponseData.content_type ? dispatchResponseData.content_type : ''
    consoleLog(
      'Dispatch thumbnail response received. files=' + String(dispatchFiles) + ', content_type=' + String(dispatchContentType)
    )

    const generatedImage = extractGeneratedImageFromDispatchResponse(dispatchResponseData)
    if (!generatedImage) {
      await uploadSourceThumbnailFallback(
        ctx,
        jigId,
        sourceThumbnailData,
        'dispatch response did not contain a generated image payload',
        jigVersion,
        isFirstThumbnail
      )
      return
    }

    const detectedImageMimeType = detectImageFormatFromBuffer(generatedImage.imageBuffer)
    if (detectedImageMimeType === 'unknown') {
      consoleError('Generated thumbnail payload is not a valid supported image binary. Upload skipped.')
      await uploadSourceThumbnailFallback(
        ctx,
        jigId,
        sourceThumbnailData,
        'generated image payload failed binary image validation',
        jigVersion,
        isFirstThumbnail
      )
      return
    }
    if (detectedImageMimeType !== 'image/png') {
      consoleLog(
        'Generated thumbnail is non-PNG; falling back to source thumbnail. detected=' +
        detectedImageMimeType +
        ', response=' +
        String(generatedImage.mimeType || '')
      )
      await uploadSourceThumbnailFallback(
        ctx,
        jigId,
        sourceThumbnailData,
        'generated image payload is not PNG',
        jigVersion,
        isFirstThumbnail
      )
      return
    }

    if (!jigVersion) {
      return
    }

    await uploadThumbnailToJig(
      ctx,
      jigId,
      jigVersion,
      generatedImage.imageBuffer,
      isFirstThumbnail
    )
  } catch (_error) {
    if (isRequestCanceled(_error)) {
      throw _error
    }
    consoleError('Error generating/uploading thumbnail:', _error)
    if (sourceThumbnailData) {
      try {
        await uploadSourceThumbnailFallback(
          ctx,
          jigId,
          sourceThumbnailData,
          'exception during generation/upload path',
          jigVersion,
          isFirstThumbnail
        )
      } catch (_fallbackError) {
        consoleError('Error uploading fallback source thumbnail:', _fallbackError)
      }
    }
  }
}
